import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import type {IssueViewerQueries} from '@github-ui/issue-viewer/IssueViewer'
import {JobInfoWithSubscription} from '@github-ui/issues-bulk-actions/JobInfoWithSubscription'
import type {AppPayload} from '@github-ui/issues-shared/app-payload'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {RelayCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {StaleCacheBanner} from '@github-ui/stale-cache-banner'
import {useEffect} from 'react'
import {type EntryPointComponent, useLazyLoadQuery} from 'react-relay'

import {IssueDetail} from '../components/show/IssueDetail'
import {useCurrentViewId, useQueryBulk, useSetCurrentViewId} from '../contexts/QueryContext'
import {IssueIndexPage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {ClientSideRelayDataGeneratorViewQuery} from './__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'
import {currentViewQuery as currentView} from './ClientSideRelayDataGenerator'
import styles from './IssueShowPage.module.css'

export const IssueShowPage: EntryPointComponent<IssueViewerQueries, Record<string, never>> = ({
  queries: issueViewerPreloadedQueries,
}) => {
  const {scoped_repository} = useAppPayload<AppPayload>()
  const currentViewId = useCurrentViewId()
  const setCurrentViewId = useSetCurrentViewId()
  const {bulkJobId, setBulkJobId} = useQueryBulk()

  const viewId = selectViewId()
  useEffect(() => {
    if (scoped_repository && !currentViewId) {
      setCurrentViewId(viewId)
    }
  }, [scoped_repository, currentViewId, setCurrentViewId, viewId])

  const currentViewNode = useLazyLoadQuery<ClientSideRelayDataGeneratorViewQuery>(
    currentView,
    {id: viewId},
    {fetchPolicy: 'store-only'},
  )

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueIndexPage.preload()
    })
  }, [])

  if (!currentViewNode.node?.shortcut) {
    return null
  }

  return (
    <AnalyticsWrapper category="Issue Show">
      <StaleCacheBanner />
      <JobInfoWithSubscription bulkJobId={bulkJobId} setBulkJobId={setBulkJobId}>
        <div className={styles.issueDetailContainer}>
          <RelayCachePreheatContextProvider>
            <IssueDetail fetchedView={currentViewNode.node.shortcut} preloadedQueries={issueViewerPreloadedQueries} />
          </RelayCachePreheatContextProvider>
        </div>
      </JobInfoWithSubscription>
    </AnalyticsWrapper>
  )

  function selectViewId() {
    if (currentViewId) {
      return currentViewId
    }
    return scoped_repository ? VIEW_IDS.repository : VIEW_IDS.assignedToMe
  }
}
